/** 支持的尺寸显示单位。 */
export const DISPLAY_UNITS = ['m', 'cm', 'mm', 'in', 'ft'] as const

/** 支持的英尺显示格式。 */
export const FEET_FORMATS = ['decimal', 'feet-inches'] as const

/** 支持的尺寸显示单位。 */
export type DisplayUnit = (typeof DISPLAY_UNITS)[number]

/** 支持的英尺显示格式。 */
export type FeetFormat = (typeof FEET_FORMATS)[number]

/** 引擎实例的尺寸单位偏好。 */
export interface UnitPreference {
  displayUnit: DisplayUnit
  feetFormat: FeetFormat
}

/** 统一单位格式化结果。 */
export interface FormattedUnitValue {
  valueText: string
  unitText: string
  displayText: string
  copyText: string
}

/** 默认尺寸单位偏好。 */
export const DEFAULT_UNIT_PREFERENCE: Readonly<UnitPreference> = Object.freeze({
  displayUnit: 'm',
  feetFormat: 'decimal',
})

const LENGTH_FACTORS_FROM_METERS: Record<DisplayUnit, number> = {
  m: 1,
  cm: 100,
  mm: 1000,
  in: 39.37007874,
  ft: 3.280839895,
}

const UNIT_PRECISIONS: Record<DisplayUnit, number> = {
  m: 3,
  cm: 1,
  mm: 0,
  in: 2,
  ft: 2,
}

const AREA_UNIT_TEXT: Record<DisplayUnit, string> = {
  m: 'm²',
  cm: 'cm²',
  mm: 'mm²',
  in: 'in²',
  ft: 'ft²',
}

const PURE_NUMBER_PATTERN = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/
const INCHES_PER_FOOT = 12
const SIXTEENTHS_PER_INCH = 16

/** 判断值是否为受支持的尺寸单位。 */
export function isDisplayUnit(value: unknown): value is DisplayUnit {
  return typeof value === 'string' && DISPLAY_UNITS.includes(value as DisplayUnit)
}

/** 判断值是否为受支持的英尺格式。 */
export function isFeetFormat(value: unknown): value is FeetFormat {
  return typeof value === 'string' && FEET_FORMATS.includes(value as FeetFormat)
}

/** 规范化来自外部设置的单位偏好。 */
export function normalizeUnitPreference(
  preference?: Partial<UnitPreference> | null
): UnitPreference {
  return {
    displayUnit: isDisplayUnit(preference?.displayUnit)
      ? preference.displayUnit
      : DEFAULT_UNIT_PREFERENCE.displayUnit,
    feetFormat: isFeetFormat(preference?.feetFormat)
      ? preference.feetFormat
      : DEFAULT_UNIT_PREFERENCE.feetFormat,
  }
}

/** 将米制长度换算为指定显示单位。 */
export function convertLengthFromMeters(valueInMeters: number, unit: DisplayUnit): number {
  return valueInMeters * LENGTH_FACTORS_FROM_METERS[unit]
}

/** 将指定显示单位的长度换算回米。 */
export function convertLengthToMeters(value: number, unit: DisplayUnit): number {
  return value / LENGTH_FACTORS_FROM_METERS[unit]
}

/** 将米换算为毫米。 */
export function metersToMillimeters(valueInMeters: number): number {
  return valueInMeters * LENGTH_FACTORS_FROM_METERS.mm
}

/** 将毫米换算为米。 */
export function millimetersToMeters(valueInMillimeters: number): number {
  return valueInMillimeters / LENGTH_FACTORS_FROM_METERS.mm
}

/** 将平方米换算为指定单位对应的平方单位。 */
export function convertAreaFromSquareMeters(
  valueInSquareMeters: number,
  unit: DisplayUnit
): number {
  const factor = LENGTH_FACTORS_FROM_METERS[unit]
  return valueInSquareMeters * factor * factor
}

/** 将指定平方单位的面积换算回平方米。 */
export function convertAreaToSquareMeters(value: number, unit: DisplayUnit): number {
  const factor = LENGTH_FACTORS_FROM_METERS[unit]
  return value / (factor * factor)
}

/** 将当前显示单位下的纯数字输入解析为米，非法输入返回 null。 */
export function parseDisplayLength(
  input: string | number,
  preference: UnitPreference
): number | null {
  const inputText = typeof input === 'number' ? String(input) : input.trim()
  if (!PURE_NUMBER_PATTERN.test(inputText)) {
    return null
  }

  const value = Number(inputText)
  if (!Number.isFinite(value)) {
    return null
  }

  return convertLengthToMeters(value, normalizeUnitPreference(preference).displayUnit)
}

/** 按单位偏好格式化米制长度。 */
export function formatLength(
  valueInMeters: number,
  preference: UnitPreference
): FormattedUnitValue {
  const normalizedPreference = normalizeUnitPreference(preference)
  const value = convertLengthFromMeters(valueInMeters, normalizedPreference.displayUnit)
  const precision = UNIT_PRECISIONS[normalizedPreference.displayUnit]
  const copyText = formatFixed(value, precision)

  if (
    normalizedPreference.displayUnit === 'ft' &&
    normalizedPreference.feetFormat === 'feet-inches'
  ) {
    const valueText = formatFeetInches(valueInMeters)
    return {
      valueText,
      unitText: 'ft',
      displayText: valueText,
      copyText,
    }
  }

  const valueText = copyText
  const unitText = normalizedPreference.displayUnit
  return {
    valueText,
    unitText,
    displayText: `${valueText} ${unitText}`,
    copyText,
  }
}

/** 按单位偏好格式化平方米面积。 */
export function formatArea(
  valueInSquareMeters: number,
  preference: UnitPreference
): FormattedUnitValue {
  const normalizedPreference = normalizeUnitPreference(preference)
  const unit = normalizedPreference.displayUnit
  const value = convertAreaFromSquareMeters(valueInSquareMeters, unit)
  const valueText = formatFixed(value, UNIT_PRECISIONS[unit])
  const unitText = AREA_UNIT_TEXT[unit]

  return {
    valueText,
    unitText,
    displayText: `${valueText} ${unitText}`,
    copyText: valueText,
  }
}

function formatFixed(value: number, precision: number): string {
  if (!Number.isFinite(value)) {
    return String(value)
  }

  const rounded = Number(value.toFixed(precision))
  const normalized = Object.is(rounded, -0) ? 0 : rounded
  return normalized.toFixed(precision)
}

function formatFeetInches(valueInMeters: number): string {
  const totalInches = Math.abs(convertLengthFromMeters(valueInMeters, 'in'))
  const totalSixteenths = Math.round(totalInches * SIXTEENTHS_PER_INCH)
  const sixteenthsPerFoot = INCHES_PER_FOOT * SIXTEENTHS_PER_INCH
  const feet = Math.floor(totalSixteenths / sixteenthsPerFoot)
  const remainingSixteenths = totalSixteenths % sixteenthsPerFoot
  const inches = Math.floor(remainingSixteenths / SIXTEENTHS_PER_INCH)
  const numerator = remainingSixteenths % SIXTEENTHS_PER_INCH
  const sign = valueInMeters < 0 && totalSixteenths !== 0 ? '-' : ''

  if (numerator === 0) {
    return `${sign}${feet}′-${inches}″`
  }

  const divisor = greatestCommonDivisor(numerator, SIXTEENTHS_PER_INCH)
  const reducedNumerator = numerator / divisor
  const reducedDenominator = SIXTEENTHS_PER_INCH / divisor
  const inchesText =
    inches === 0
      ? `${reducedNumerator}/${reducedDenominator}`
      : `${inches} ${reducedNumerator}/${reducedDenominator}`
  return `${sign}${feet}′-${inchesText}″`
}

function greatestCommonDivisor(left: number, right: number): number {
  let a = left
  let b = right
  while (b !== 0) {
    const remainder = a % b
    a = b
    b = remainder
  }
  return a
}
